'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigationItems = [
    { id: 'problem-statement', label: 'The Problem', href: '#problem-statement' },
    {
      id: 'product',
      label: 'Product',
      href: '#solution-overview',
      hasDropdown: true,
      subItems: [
        { id: 'key-benefits', label: 'Benefits', href: '#key-benefits' },
        { id: 'device-technology', label: 'Technology', href: '#device-technology' },
        { id: 'clinical-evidence', label: 'Clinical Evidence', href: '#clinical-evidence' },
      ],
    },
    { id: 'team', label: 'Team', href: '#team' },
    { id: 'faq', label: 'FAQ', href: '#faq' },
    { id: 'contact', label: 'Contact Us', href: '#contact', isPrimary: true },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const allSections = navigationItems.flatMap((item) =>
        item.hasDropdown && item.subItems ? [item.id, ...item.subItems.map((sub) => sub.id)] : [item.id]
      );
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of allSections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, id: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsProductDropdownOpen(false);

    const element = document.querySelector(href);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const isProductActive =
    activeSection === 'product' ||
    activeSection === 'key-benefits' ||
    activeSection === 'device-technology' ||
    activeSection === 'clinical-evidence' ||
    activeSection === 'solution-overview';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-250 ease-out ${
        isScrolled ? 'bg-card shadow-md' : 'bg-card'
      }`}
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between h-20 px-6">
          <Link href="/" className="flex items-center space-x-3">
            <AppImage
              src="/assets/images/Sensivision_Logo_SVHT_Blue_Mid-1766901565427.png"
              alt="Sensivision Health Technologies Pvt. Ltd. company logo with blue branding and medical technology design"
              className="h-12 w-auto object-contain"
              priority={true}
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) =>
              item.hasDropdown ? (
                <div key={item.id} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
                    className={`px-4 py-2 rounded-2xl font-medium transition-all duration-250 ease-out flex items-center gap-1 ${
                      isProductActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.label}
                    <Icon
                      name="ChevronDownIcon"
                      size={16}
                      className={`transition-transform duration-200 ${isProductDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isProductDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-2xl shadow-lg py-2 min-w-[200px]">
                      <a
                        href={item.href}
                        onClick={(e) => handleNavClick(e, item.href, 'solution-overview')}
                        className="block px-4 py-2 text-foreground hover:bg-muted transition-colors duration-200"
                      >
                        Overview
                      </a>
                      {item.subItems?.map((subItem) => (
                        <a
                          key={subItem.id}
                          href={subItem.href}
                          onClick={(e) => handleNavClick(e, subItem.href, subItem.id)}
                          className="block px-4 py-2 text-foreground hover:bg-muted transition-colors duration-200"
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href, item.id)}
                  className={`px-4 py-2 rounded-2xl font-medium transition-all duration-250 ease-out ${
                    item.isPrimary
                      ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-cta font-cta'
                      : activeSection === item.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {item.label}
                </a>
              )
            )}
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-2xl hover:bg-muted transition-colors duration-250 ease-out"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} className="text-foreground" />
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border">
            <nav className="flex flex-col py-4 px-6 space-y-2">
              {navigationItems.map((item) =>
                item.hasDropdown ? (
                  <div key={item.id}>
                    <a
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href, 'solution-overview')}
                      className={`block px-4 py-3 rounded-2xl font-medium transition-all duration-250 ease-out ${
                        isProductActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      {item.label}
                    </a>
                    <div className="ml-4 mt-2 space-y-2">
                      {item.subItems?.map((subItem) => (
                        <a
                          key={subItem.id}
                          href={subItem.href}
                          onClick={(e) => handleNavClick(e, subItem.href, subItem.id)}
                          className={`block px-4 py-2 rounded-2xl text-sm transition-all duration-250 ease-out ${
                            activeSection === subItem.id ? 'bg-primary/20 text-primary' : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href, item.id)}
                    className={`px-4 py-3 rounded-2xl font-medium transition-all duration-250 ease-out ${
                      item.isPrimary
                        ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-cta font-cta text-center'
                        : activeSection === item.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.label}
                  </a>
                )
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;